import React, { Fragment, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";

import { CheckIcon, TrashIcon, ChevronDownIcon } from "components/Icons";
import FormField from "components/MeForm/FormField";
import upStore from "store/upStore";
import { classNames, colors } from "tools/ui";
import proxy from "api/proxy";

const style =
  "border border-slate-300 appearance-none pt-2 pb-2 px-2 w-full rounded-md focus:outline-none focus:border focus:border-cyan-500 h-10 bg-white";

// const people = [
//   { id: 1, name: 'Jhon McClane' },
//   More users...
// ]

var timeoutId;

/**
 * Field Form Selection.
 *
 * @param {object} props - component props
 * @param {string} props.name - field name
 * @param {string} props.label - field label
 * @param {boolean} props.readOnly - whether field is read-only
 * @param {string} props.model - model name to search for options
 * @param {string} props.placeholder - string for view in placeholder
 * @param {function} props.recSearch - function to generate search domain based on query and current record
 * @param {Array} props.options - list of options if model is not provided
 * @param {Array} props.attrs - list of fields to include in the option objects if model is provided
 * @param {object} props.record - current record object
 * @param {function} props.onChange - callback function called when field value changes
 * @param {any} props.value - field value
 * @return {Component} - selection component
 */
export default function DropdownField(props) {
  const { field, attrs, data } = props;
  const { name, model, readOnly, recSearch, placeholder } = field;

  const { level, record, parentRec } = attrs;

  const [options, setOptions] = useState([]);
  const field_ = field.recName || "rec_name";

  let fieldsNames = ["id", field_];
  if (field.attrs) {
    fieldsNames = [...fieldsNames, ...field.attrs];
  }

  async function getOptions(_data) {
    let limit = field.limit || 300;
    let domain = [];
    if (recSearch) {
      let _parent = parentRec ? parentRec.record : null;
      let domainAdd = await recSearch(_data, record, _parent);
      if (domainAdd === null) return;
      domain.push(domainAdd);
    }
    if (_data && _data !== "") {
      let clause = ["OR"];
      const target_words = _data.split(" ");
      target_words.forEach((tw) => {
        if (tw.length <= 1) return;
        clause.push([field_, "ilike", `%${tw}%`]);
      });
      domain.push(clause);
    }
    const { data: res } = await proxy.search(model, domain, fieldsNames, limit);
    setOptions(res);
  }

  const onOpen = () => {
    getOptions();
  };

  function onRemove() {
    handleChange(null);
  }

  function handleChange(value) {
    data.value = value;
    upStore(level, {
      field: field,
      fieldName: name,
      value: value,
      valueActive: value,
      parentRec: parentRec,
    });
  }

  function onChangeInput(event) {
    const data = event.target.value;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (data?.length > 2) {
      timeoutId = setTimeout(getOptions, 500, data);
    }
  }

  let color = "";
  let styleDisabled = "";
  let chevronIconBg = "bg-white";
  if (readOnly) {
    color = colors.readOnly;
    chevronIconBg = colors.readOnly;
    styleDisabled = "pointer-events-none";
  }

  let nameRec = "";
  if (data && data.value && typeof data.value === "object") {
    nameRec = data.value.name ?? data.value.rec_name;
    if (data.value?.reference && data.value.reference !== "") {
      nameRec += data.value.reference;
    }
  } else if (record) {
    let _name = name;
    if (record && typeof record[name] === "number") {
      _name = `${name}.`;
    }
    if (record[_name]) {
      nameRec = record[_name].name || record[_name].rec_name;
    }
  }

  return (
    <FormField {...field}>
      <Combobox
        as="div"
        className="flex"
        value={nameRec}
        onChange={handleChange}
      >
        <div className="relative w-full">
          <ComboboxInput
            disabled={readOnly}
            readOnly={readOnly}
            className={classNames(style, color)}
            onChange={onChangeInput}
            displayValue={nameRec}
            placeholder={placeholder}
          />
          <ComboboxButton
            disabled={readOnly}
            readOnly={readOnly}
            onClick={onOpen}
            className={classNames(
              "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none",
              styleDisabled,
            )}
          >
            <ChevronDownIcon
              className={classNames("h-5 w-5 text-neutral-400", chevronIconBg)}
              aria-hidden="true"
            />
          </ComboboxButton>

          {options?.length > 0 && (
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-neutral-200 ring-opacity-5 focus:outline-none sm:text-sm">
              {options.map((option) => (
                <ComboboxOption
                  key={option.id}
                  value={option}
                  className={
                    "relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-cyan-500 data-[focus]:text-white"
                  }
                >
                  {({ selected }) => (
                    <Fragment>
                      <span
                        className={classNames(
                          "block truncate",
                          selected && "font-semibold",
                        )}
                      >
                        {option.name || option.rec_name}
                      </span>

                      <span
                        className={
                          "absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 data-[focus]:text-white"
                        }
                      >
                        <CheckIcon
                          className="h-5 w-5 fill-current invisible group-data-[selected]:visible"
                          aria-hidden="true"
                        />
                      </span>
                    </Fragment>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </div>
        {!readOnly && (
          <TrashIcon
            onClick={onRemove}
            className="mx-2 h-6 w-6 my-auto text-neutral-500 active:text-sky-600 cursor-pointer active:scale-90 fill-current"
            aria-hidden="true"
          />
        )}
      </Combobox>
    </FormField>
  );
}
